// M004-F0636 (docs/design/M004-F0636-Envelopes-Tab.md) — pure model helpers
// for the Envelopes tab and its side drawer. Data authority: Spec
// `core-ledger-bookkeeping` 1.2.0 closed schemas (EnvelopeRecord oneOf
// open/closed/deleted). The retention clock (days_remaining, expiring,
// expired, purge_after) is SERVER-DERIVED state — nothing here computes a
// countdown in the browser; the renderers only read and format those fields.
import { envelopeApi } from '../../services/envelopeApi';

export const ENVELOPES_TAB_COPY = "Envelopes — receipts you're keeping track of, not on the ledger.";
export const NO_SUCH_ENVELOPE_TEXT = 'No such envelope';
export const PROMOTE_CONFIRM_TEXT = "Move this receipt onto the organisation's receipt queue? It leaves this envelope.";
export const CLOSED_UPLOAD_NOTICE = 'This envelope is closed — reopen it to add receipts';

export const extractEnvelopeError = (cause) => {
  const data = cause?.response?.data;
  const code = typeof data?.error === 'string' && data.error ? data.error : '';
  const message = (typeof data?.message === 'string' && data.message) || cause?.message || 'StudioAEye could not reach the Envelopes service.';
  const status = Number(cause?.response?.status) || 0;
  return { code, message, status };
};

export const isEnvelopeNotFound = (cause) => {
  const { code, status } = extractEnvelopeError(cause);
  return status === 404 || code === 'ENVELOPE_NOT_FOUND';
};

export const sortEnvelopes = (records) => {
  const list = Array.isArray(records) ? [...records] : [];
  const byUpdatedDesc = (a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || ''));
  const open = list.filter((record) => record.status !== 'deleted').sort(byUpdatedDesc);
  const deleted = list.filter((record) => record.status === 'deleted')
    .sort((a, b) => String(b.deleted_at || '').localeCompare(String(a.deleted_at || '')));
  return open.concat(deleted);
};

export const sortReceipts = (receipts) => (
  (Array.isArray(receipts) ? [...receipts] : [])
    .sort((a, b) => String(a.captured_at || '').localeCompare(String(b.captured_at || '')))
);

export const statusChip = (record) => {
  if (!record) return { label: '', tone: 'open' };
  if (record.status === 'open') return { label: 'Open', tone: 'open' };
  if (record.status === 'closed') return { label: 'Closed', tone: 'closed' };
  if (record.status === 'deleted') {
    const days = Number(record.days_remaining);
    const suffix = record.expiring ? ' ⚠' : '';
    return {
      label: `Purges in ${Number.isFinite(days) ? days : 0} days${suffix}`,
      tone: record.expiring ? 'warning' : 'deleted',
    };
  }
  return { label: String(record.status), tone: 'open' };
};

export const countdownText = (record) => {
  const days = Number(record?.days_remaining) || 0;
  const date = String(record?.purge_after || '').slice(0, 10);
  return `Purges in ${days} days (on ${date})`;
};

export const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const formatDateRange = (from, to) => {
  if (from && to) return `${from} – ${to}`;
  return from || to || '';
};

export const formatStamp = (iso) => (iso ? String(iso).slice(0, 10) : '');

export const formatDateTime = (iso) => (iso ? `${String(iso).slice(0, 10)} ${String(iso).slice(11, 16)}` : '');

const CAPTURE_BADGES = { exif: 'EXIF', client: 'file time', intake: 'intake' };
export const captureBadge = (origin) => CAPTURE_BADGES[origin] || 'intake';

export const parseAmountInput = (text, defaultCurrency = 'USD') => {
  const match = /^\s*(\d{1,10})(?:\.(\d{1,2}))?\s*([A-Za-z]{3})?\s*$/.exec(String(text || ''));
  if (!match) return null;
  const whole = match[1];
  const fraction = (match[2] || '').padEnd(2, '0');
  const currency = (match[3] || defaultCurrency).toUpperCase();
  return { minor: Number(whole + fraction), currency };
};

export const formatAmount = (amount) => {
  if (!amount || !Number.isFinite(Number(amount.minor))) return '';
  const minor = Number(amount.minor);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  const text = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return `${sign}${text} ${amount.currency || 'USD'}`;
};

export const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const subtle = () => {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  if (!c || !c.subtle) throw new Error('SHA-256 verification is unavailable in this browser.');
  return c.subtle;
};

export const sha256Hex = async (bytes) => {
  const digest = await subtle().digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
};

const decodeBase64ToBytes = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const concatBytes = (parts) => {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};

const chunkData = (response) => response?.data?.data || response?.data || {};

// GET …/archive.zip is a PULL-PER-CHUNK protocol (Spec core-ledger-bookkeeping
// 1.2.0, downloadEnvelopeArchive): the first request (no params) builds the
// STORE zip server-side and returns {size, sha256, build_token, offset,
// chunk_bytes, chunk(base64)}; each further request carries offset + build.
// 409 ARCHIVE_STALE (Ledger restart, temp TTL, any write since the build)
// restarts the build from offset 0 — once; a second stale answer surfaces.
// The client verifies sha256 over exactly size bytes and NEVER hands the file
// to the user on a mismatch.
export const downloadEnvelopeArchive = async (orgId, envelopeId, { sha256 = sha256Hex, pullChunk } = {}) => {
  const pull = async (params) => chunkData(pullChunk ? await pullChunk(params) : await envelopeApi.archive(orgId, envelopeId, params));
  let attempt = 0;
  for (;;) {
    let first;
    try {
      first = await pull(undefined);
    } catch (cause) {
      if (attempt === 0 && extractEnvelopeError(cause).code === 'ARCHIVE_STALE') {
        attempt += 1;
        continue;
      }
      throw cause;
    }
    const size = Number(first.size);
    const expectedSha = String(first.sha256 || '');
    const buildToken = String(first.build_token || '');
    const parts = [decodeBase64ToBytes(first.chunk)];
    let received = Number(first.chunk_bytes) || 0;
    let stale = false;
    while (received < size) {
      let next;
      try {
        next = await pull({ offset: received, build: buildToken });
      } catch (cause) {
        if (attempt === 0 && extractEnvelopeError(cause).code === 'ARCHIVE_STALE') {
          stale = true;
          break;
        }
        throw cause;
      }
      parts.push(decodeBase64ToBytes(next.chunk));
      received += Number(next.chunk_bytes) || 0;
    }
    if (stale) {
      attempt += 1;
      continue;
    }
    const bytes = concatBytes(parts);
    if (bytes.length !== size) {
      const error = new Error('The envelope archive is incomplete — download aborted.');
      error.code = 'ARCHIVE_INCOMPLETE';
      throw error;
    }
    const actualSha = await sha256(bytes);
    if (actualSha !== expectedSha) {
      const error = new Error('The envelope archive failed its integrity check — download aborted.');
      error.code = 'SHA256_MISMATCH';
      throw error;
    }
    return bytes;
  }
};

export const saveBlobDownload = (blob, filename) => {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function' || typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
};

export const mergeEnvelopeRecord = (records, record) => {
  if (!record?.envelope_id) return Array.isArray(records) ? records : [];
  const rest = (Array.isArray(records) ? records : []).filter((item) => item.envelope_id !== record.envelope_id);
  return sortEnvelopes(rest.concat(record));
};
